import {
  AfterViewInit,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {CommonModule, DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {catchError, debounceTime, of, Subject, take, takeUntil} from 'rxjs';

// PrimeNG imports
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {SelectModule} from 'primeng/select';
import {MultiSelectModule} from 'primeng/multiselect';
import {DatePickerModule} from 'primeng/datepicker';
import {CheckboxModule} from 'primeng/checkbox';
import {TooltipModule} from 'primeng/tooltip';
import {MessageService} from 'primeng/api';
import {ToastModule} from 'primeng/toast';
import {DialogService, DynamicDialogModule} from 'primeng/dynamicdialog';

// Components
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {MapControllerInput, MapInitOptions} from '@components/openstreetmap/interfaces';
import {ListPageWrapper} from '@app/wrappers/pages/list-page-wrapper/list-page-wrapper';
import {Filters} from '@components/views/filters/filters';

// Services
import {FieldManager} from './managers/field-manager.service';
import {CropIaManager} from './managers/crop-ia-manager.service';

import {DateFormatLangPipe} from '@app/utils/pipes/date-format-lang-pipe';
import {CropCacheService} from '@services/caches/crop-cache.service';
import {
  ImportFieldModalComponent
} from '@app/pages/electronic-field-map/pages/import-fields/modals/import-field-modal/import-field-modal';
import {ExportModalComponent} from '@components/export-modal/export-modal.component';
import {Field, FieldService, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {
  ModalAcceptanceIdentification
} from '@app/pages/crop-identification/pages/gis-crop-identification/components/modals/modal-acceptance/modal-acceptance-identification';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {IMsfFormBuilder} from '@components/form-builder/form-builder';
import {buildFilterQuery} from '@app/utils/_msf';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {CropIdentificationMapItemDto} from '@6grain/masofa-monolith-api-client/ai-inference';
import {FilterOperator} from '@app/utils/filter-operator';
import {FieldSourceType, IdentificationCropSources} from '@models/IFieldSource';
import {environment} from '@root/src/environments/environment';
import {BackButton} from '@components/ui/back-button/back-button';

@Component({
  selector: 'app-gis-crop-identification',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    DialogModule,
    DynamicDialogModule,
    SelectModule,
    MultiSelectModule,
    DatePickerModule,
    CheckboxModule,
    TooltipModule,
    ToastModule,
    OpenStreetMapComponent,
    ListPageWrapper,
    Filters,
    ImportFieldModalComponent,
    ExportModalComponent,
    BackButton,
  ],
  providers: [
    MessageService,
    DatePipe,
    DateFormatLangPipe,
    DialogService
  ],
  templateUrl: './gis-crop-identification.html',
  styleUrls: ['./gis-crop-identification.scss']
})
export class GisCropIdentification implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(OpenStreetMapComponent) openstreetmapComponent!: OpenStreetMapComponent;

  public regionManagementManager = inject(RegionManagerService);
  public fieldManager = inject(FieldManager);
  public cropIaManager = inject(CropIaManager);
  public cropCacheService = inject(CropCacheService);
  public fieldService = inject(FieldService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private destroy$ = new Subject<void>();
  private pendingFocusIdentificationId: string | null = null;
  private pendingFocusFieldId: string | null = null;
  /** Re-applied after bbox reload so zoom stays at field level. */
  private pendingFocusView: { lat: number; lng: number } | null = null;
  /** Blocks debounced mapMove reload while focus-from-list runs (avoids layer wipe / double bbox). */
  private blockMapMoveReload = false;
  private static readonly FIELD_FOCUS_ZOOM = 16;
  public showImportModal: boolean = false;
  public showModalExport = false;

  public exportService: WritableSignal<FieldService | SeasonService | null> = signal(null);
  public exportMethod = signal('');
  public exportCsvMethod = signal('');


  mapController$ = new Subject<MapControllerInput>();

  mapOptions: MapInitOptions = {
    height: '100%',
    center: [environment.map.lat, environment.map.lng],
    zoom: 7,
    showControls: true,
    minimalControls: false,
    drawingMode: false,
    showDrawingControls: true,
    drawingControlsDirection: 'row',
    baseMap: 'osm',
    showRegionNames: false,
    disableBounds: false,
    indexOpacityToggle: true,
  };

  // =========================================================================
  // UI STATE
  // =========================================================================
  selectedWeatherLayer: string | null = null;
  showContextMenu: boolean = false;
  contextMenuPosition = {top: 0, left: 0};
  contextMenuData: any = null;


  mapZoom: number = 6;
  currentDrawingState = {
    isDrawing: false,
    drawingType: null as 'point' | 'polygon' | 'field' | 'rectangle' | null
  };

  filterMetaData: any = {};
  selectedDataSource: string | null = null;
  chartData: any = {labels: [], datasets: []};
  cropPanelStyle: any = {top: '120px', right: '380px'};
  showCropIdentificationPanel: boolean = false;
  filterModel: any = {type: 'seasons'};
  public filtersForms: IMsfFormBuilder[] = [];
  public filtersParams: Array<any> = [];
  /** Total identifications matching current filters (same API as list). */
  readonly totalRecords = signal(0);
  private toggleFilter: boolean = false
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);


  @Input() panelOptions: { crops?: boolean } = {
    crops: true,
  };
  private selectedRegionId: any;

  ngOnInit(): void {
    this.exportService.set(this.fieldManager.fieldService);
    this.exportMethod.set('cropMonitoringFieldExportFieldsPost');
    this.exportCsvMethod.set('cropMonitoringFieldExportFromCSVPost');
    this.filtersParams = this.getDefaultFilter();
    this.refreshIdentificationTotalCount();
    this.initializeComponent();
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const focusId = params.get('focusIdentificationId');
      if (!focusId) {
        return;
      }
      this.pendingFocusIdentificationId = focusId;
      this.pendingFocusFieldId = params.get('focusFieldId');
      this.tryFocusIdentificationFromQuery(0);
    });
  }

  /** Same default as `crop-identification-list`. */
  protected getDefaultFilter(): Object[] {
    return [
      {
        filterField: 'topProbability',
        filterOperator: FilterOperator.GreaterThan,
        filterValue: 0.59,
      },
    ];
  }

  private refreshIdentificationTotalCount(): void {
    const filters = this.filtersParams.filter((f: any) => f.filterField !== 'type');
    this.cropIaManager.cropIdentificationService
      .aiInferenceCropIdentificationGetTotalCountPost({filters: filters as any})
      .pipe(takeUntil(this.destroy$), catchError(() => of(0)))
      .subscribe((total) => this.totalRecords.set(Number(total) || 0));
  }

  initFiltersForm(): void {
    this.filtersForms = [
      {
        name: 'identificationDate',
        title: 'Date',
        type: 'date',
        placeholder: 'Select period',
        options: {
          date: {dateOnly: true},
        },
      },
      {
        name: 'fieldId',
        title: 'Field name',
        type: 'select-paginate',
        selectService: this.fieldService,
        selectServiceMethod: 'cropMonitoringFieldGetByQueryPost',
        placeholder: 'Enter field name',
        options: {
          select: {
            optionLabel: 'name',
            optionValue: 'id',
            searchBy: 'name',
            translateBy: 'none'
          }
        },
      },
      {
        name: 'CropId',
        title: 'Culture',
        type: 'select-paginate',
        selectService: this.cropCacheService,
        selectServiceMethod: 'getCrops',
        options: {
          select: {
            searchBy: 'names',
          }
        },
      },
      {
        title: 'Region',
        name: 'regionId',
        type: 'tree-select',
        selectService: this.regionManagementManager,
        selectServiceMethod: 'getRegionsObservable',
        options: {
          treeSelect: {
            treeBy: 'parentId',
          }
        },
        placeholder: 'Select region',
      },
      {
        name: 'source',
        title: 'Source',
        type: 'select',
        placeholder: 'Select source',
        selectOptions: IdentificationCropSources,
        options: {
          clearable: false,
          select: {optionLabel: 'name', translateBy: 'locale'}

        }
      },
    ];
  }


  ngAfterViewInit(): void {

    setTimeout(() => {
      this.loadRegions();
      this.loadInitialData();
      this.subscribeToMapEvents();
    }, 500);

    setTimeout(() => {
      this.initFiltersForm();
    }, 1000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize component
   */
  private initializeComponent(): void {
    // Set initial map options
    this.mapOptions.center = [environment.map.lat, environment.map.lng];
    this.mapOptions.zoom = 6;

  }

  /**
   * Subscribe to map events including right-click context menu
   */
  private subscribeToMapEvents(): void {
    // Subscribe to map right-click events
    if (this.openstreetmapComponent?.mapRightClick$) {
      this.openstreetmapComponent.mapRightClick$
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: any) => {
          this.handleMapRightClick(event);
        });
    }


    // Subscribe to polygon and marker clicks to show field modal
    try {
      if (this.openstreetmapComponent?.polygonClick$) {
        this.openstreetmapComponent.polygonClick$
          .pipe(takeUntil(this.destroy$))
          .subscribe((evt: any) => {
            // Don't handle polygon clicks if drawing mode is active
            if (this.openstreetmapComponent?.isDrawingRectangle() ||
              this.openstreetmapComponent?.isDrawingPolygon() ||
              this.openstreetmapComponent?.isDrawingBoxPolygon() ||
              this.openstreetmapComponent?.isDrawingCustomPolygon()) {
              return;
            }
            this.handlePolygonClick(evt);
          });
      }

      if (this.openstreetmapComponent?.markerClick$) {
        this.openstreetmapComponent.markerClick$
          .pipe(takeUntil(this.destroy$))
          .subscribe((evt: any) => {
            this.handleMarkerClick(evt);
          });
      }
    } catch (err) {
      console.warn('Error subscribing to polygon/marker clicks', err);
    }

    if (this.openstreetmapComponent?.mapMove$) {
      this.openstreetmapComponent.mapMove$
        .pipe(
          takeUntil(this.destroy$),
          debounceTime(1000) // Wait 500ms after user stops moving to avoid too many requests
        )
        .subscribe(() => {
          if (this.blockMapMoveReload) {
            return;
          }
          this.loadMapData();
        });
    }
  }


  private handleMapRightClick(event: any): void {
    // Show context menu at the click position
    this.showContextMenu = true;
    this.contextMenuPosition = {
      top: event.containerPoint?.y || 0,
      left: event.containerPoint?.x || 0
    };

    // Store the click data for context menu actions
    this.contextMenuData = {
      lat: event.lat,
      lng: event.lng,
      originalEvent: event.originalEvent,
      containerPoint: event.containerPoint
    };

    // Close context menu when clicking elsewhere on the map
    setTimeout(() => {
      document.addEventListener('click', this.onDocumentClick.bind(this), {once: true});
    }, 100);
  }

  /**
   * Close context menu when clicking outside
   */
  private onDocumentClick(event: any): void {
    const contextMenuEl = document.querySelector('.map-context-menu');
    if (!contextMenuEl?.contains(event.target)) {
      this.showContextMenu = false;
    }
  }

  /**
   * Load initial data
   */
  private loadInitialData(): void {
    // Check if map is initialized before loading data
    if (!this.openstreetmapComponent?.isMapInitialized) {
      return;
    }
    try {
      this.fieldManager.setZoomThreshold(11);
    } catch (err) {
    }
  }

  /**
   * Toggle region names display
   */
  toggleRegionNames(): void {
    this.mapController$.next({
      setShowRegionNames: this.mapOptions.showRegionNames
    })
  }


  private loadPolygons(): void {
    if (!this.openstreetmapComponent || !this.openstreetmapComponent.isMapInitialized) {
      return;
    }

    try {
      // Initialize manager with map instance
      this.fieldManager.initialize(this.openstreetmapComponent);
      this.cropIaManager.initialize(this.openstreetmapComponent);

      this.openstreetmapComponent.isLoading = true;

      // Get current bounds from map instance
      const mapInstance = this.openstreetmapComponent.getMapInstance();
      if (!mapInstance || typeof mapInstance.getBounds !== 'function') {
        return;
      }

      const bounds = mapInstance.getBounds();
      // Leaflet bounds: _southWest and _northEast
      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();


      const filtersForQuery = this.filtersParams.filter((filter: any) => filter.filterField !== 'type');

      const query = {
        filters: filtersForQuery as any
      };

      const params = {
        minLon: southWest.lng,
        minLat: southWest.lat,
        maxLon: northEast.lng,
        maxLat: northEast.lat,
        zoom: mapInstance.getZoom(),
        query: query,
      } as any;

      this.cropIaManager.loadCropIaByBbox(params)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.openstreetmapComponent.isLoading = false;
          this.reapplyPendingFieldFocusZoom();
        });

    } catch (err) {
      console.warn('Error in loadFieldsPolygons', err);
    }
  }


  loadMapData() {
    this.loadPolygons();
  }

  private loadRegions(): void {

    // Show loading indicator
    if (this.openstreetmapComponent) {
      this.openstreetmapComponent.isLoading = true;
    }

    this.regionManagementManager.toPolygon()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (polygons) => {
          // Hide loading indicator
          if (this.openstreetmapComponent) {
            this.openstreetmapComponent.isLoading = false;
          }

          if (polygons && polygons.length > 0) {

            // Configure polygon events using the manager
            this.regionManagementManager.configurePolygonEvents(
              polygons,
              this.mapOptions,
              (lat, lng, polygonData) => {
                this.selectedRegionId = polygonData?.id
                // this.loadPolygonDataAndOpenModal(lat, lng, polygonData)
              }
            );
            // Add new filtered polygons
            this.mapController$.next({
              addGisPolygons: polygons
            });

            setTimeout(() => {
              this.mapOptions.showRegionNames = true
              this.toggleRegionNames();
            });

            this.loadMapData();
            this.cropCacheService.getCrops()
              .pipe((takeUntil(this.destroy$)))
              .subscribe();
          }
        },
        error: (err) => {
          // Hide loading indicator on error
          if (this.openstreetmapComponent) {
            this.openstreetmapComponent.isLoading = false;
          }
          console.error('Error loading regions:', err);
        }
      });
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filtersParams = this.getDefaultFilter();
    this.filterModel = {
      type: 'seasons'
    };
    this.toggleFilter = !this.toggleFilter;
    this.refreshIdentificationTotalCount();
    this.resetMapFilters();
  }


  private resetMapFilters(): void {
    this.fieldManager.clearFieldLayers();
    this.cropIaManager.clearIaLayers();

    this.loadMapData()
    // Reset map view to default
    const layersController = this.openstreetmapComponent?.getLayersController();
    if (layersController) {
      layersController.centerToPoint(
        environment.map.lat,
        environment.map.lng,
        6,
        true
      );
    }
  }


  private formatFilterModelForQuery(model: Record<string, Object>): Record<string, Object> {
    const filters = {...model};
    const raw = filters['identificationDate'];
    if (raw == null || raw === '') {
      return filters;
    }
    if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) {
      filters['identificationDate'] = raw.trim();
      return filters;
    }
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
      filters['identificationDate'] = this.datePipe.transform(raw, 'yyyy-MM-dd') ?? '';
      return filters;
    }
    return filters;
  }

  applyFilters(changedModel: any) {
    const model = this.formatFilterModelForQuery(changedModel);
    this.filtersParams = [...this.getDefaultFilter(), ...buildFilterQuery(model)];
    this.refreshIdentificationTotalCount();
    this.loadMapData();
    this.regionManagementManager.filterByRegion(
      changedModel.regionId,
      this.openstreetmapComponent.getLayersController()
    );

  }


  /**
   * Get current display mode
   */
  getRegionPathString(): string {
    return this.regionManagementManager.getRegionPathString(
      this.translate.instant(environment.country),
      this.translate.instant('ALL_REGIONS')
    );
  }


  getCropIdentificationList(): any[] {
    const crops = [
      {nameKey: 'COTTON', color: '#c0c0c0'},
      {nameKey: 'WHEAT', color: '#ffe119'},
      {nameKey: 'CORN', color: '#00FFFF'},
      {nameKey: 'RICE', color: '#ffffff'},
      {nameKey: 'SOYBEAN', color: '#78A8FC'},
      {nameKey: 'SUNFLOWER', color: '#ffbf00'},
      {nameKey: 'TOMATO', color: '#e6194b'},
      {nameKey: 'POTATO', color: '#D0AB8B'},
      {nameKey: 'MUNG_BEAN', color: '#AAFFC3'},
      {nameKey: 'WATERMELON', color: '#f032e6'},
      {nameKey: 'ONION', color: '#ffcc99'},
      {nameKey: 'CARROT', color: '#ff7f00'},
      {nameKey: 'CUCUMBER', color: '#008000'},
      {nameKey: 'HOT_PEPPER', color: '#800000'},
      {nameKey: 'CHICKPEA', color: '#e6beff'},
      {nameKey: 'CABBAGE', color: '#a2c523'},
      {nameKey: 'ORCHARD_WALNUT', color: '#D8B99D'},
      {nameKey: 'ORCHARD_APPLE', color: '#bcf60c'},
      {nameKey: 'ORCHARD_VINEYARD', color: '#6750A4'},
      {nameKey: 'ORCHARD_POMEGRANATE', color: '#990033'}
    ];

    // Sort crops alphabetically based on translated name
    const currentLang = this.translate.currentLang || 'ru';
    let locale = 'ru-RU';

    if (currentLang.includes('en')) {
      locale = 'en-US';
    } else if (currentLang.includes('uz')) {
      locale = 'uz-Latn-UZ';
    }

    return crops.sort((a, b) => {
      const nameA = this.translate.instant(a.nameKey);
      const nameB = this.translate.instant(b.nameKey);

      return nameA.localeCompare(nameB, locale, {
        sensitivity: 'base',
        numeric: true,
        ignorePunctuation: true
      });
    });
  }

  /** Deep link: `/crop-identification/gis?focusIdentificationId=...&focusFieldId=...` (e.g. from list). */
  private tryFocusIdentificationFromQuery(attempt: number): void {
    const id = this.pendingFocusIdentificationId;
    if (!id) {
      return;
    }

    setTimeout(() => {
      if (!this.openstreetmapComponent?.isMapInitialized) {
        if (attempt < 40) {
          this.tryFocusIdentificationFromQuery(attempt + 1);
        }
        return;
      }

      this.focusIdentificationRow(id, this.pendingFocusFieldId);
      this.pendingFocusFieldId = null;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {focusIdentificationId: null, focusFieldId: null},
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }, attempt === 0 ? 0 : 120);
  }

  /** API GetById returns `{ entity: Field }` (same as field passport). */
  private resolveFieldEntity(response: Field | { entity?: Field } | null | undefined): Field | null {
    if (!response) {
      return null;
    }
    const wrapped = response as { entity?: Field };
    return wrapped.entity ?? (response as Field);
  }

  /** centerY = latitude, centerX = longitude (field passport convention). */
  private fieldCenterLatLng(field: Field | null | undefined): { lat: number; lng: number } | null {
    if (!field) {
      return null;
    }
    const lat = field.centerY != null ? Number(field.centerY) : NaN;
    const lng = field.centerX != null ? Number(field.centerX) : NaN;
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    return {lat, lng};
  }

  /** Lock map on field centroid at zoom {@link GisCropIdentification.FIELD_FOCUS_ZOOM}. */
  private applyFieldFocusZoom(lat: number, lng: number, animate = false): void {
    const lc = this.openstreetmapComponent?.getLayersController();
    if (!lc || Number.isNaN(lat) || Number.isNaN(lng)) {
      return;
    }
    lc.centerToPoint(lat, lng, GisCropIdentification.FIELD_FOCUS_ZOOM, animate);
  }

  private beginFocusFromListSession(): void {
    this.blockMapMoveReload = true;
    setTimeout(() => {
      this.blockMapMoveReload = false;
    }, 3000);
  }

  /** Zoom to field center then reload bbox layers (no extra highlight polygon). */
  private zoomToFieldAndLoadMap(fieldEntity: Field): void {
    const center = this.fieldCenterLatLng(fieldEntity);
    if (center) {
      this.pendingFocusView = center;
      this.applyFieldFocusZoom(center.lat, center.lng, true);
    } else {
      this.pendingFocusView = null;
    }
    setTimeout(() => this.loadMapData(), 400);
  }

  private reapplyPendingFieldFocusZoom(): void {
    const target = this.pendingFocusView;
    if (!target) {
      return;
    }
    this.applyFieldFocusZoom(target.lat, target.lng, false);
    this.pendingFocusView = null;
  }

  /** List → GIS: zoom 16, GetCropIdentificationByBbox, then open popup on the IA marker. */
  focusIdentificationRow(identificationId: string, fieldId?: string | null): void {
    if (!identificationId || !this.openstreetmapComponent?.isMapInitialized) {
      return;
    }

    this.pendingFocusIdentificationId = identificationId;
    this.cropIaManager.setPendingFocusPopupId(identificationId);
    this.beginFocusFromListSession();

    const loadFieldAndZoom = (resolvedFieldId: string) => {
      this.fieldService.cropMonitoringFieldGetByIdIdGet(resolvedFieldId)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const fieldEntity = this.resolveFieldEntity(response);
            if (fieldEntity) {
              this.zoomToFieldAndLoadMap(fieldEntity);
            } else {
              this.pendingFocusView = null;
              this.loadMapData();
            }
          },
          error: () => {
            this.pendingFocusView = null;
            this.loadMapData();
          },
        });
    };

    if (fieldId) {
      loadFieldAndZoom(fieldId);
      return;
    }

    this.cropIaManager.cropIdentificationService
      .aiInferenceCropIdentificationGetExtendedByIdIdGet(identificationId)
      .pipe(take(1), takeUntil(this.destroy$), catchError(() => of(null)))
      .subscribe((result) => {
        const resolvedFieldId = result?.fieldId ?? result?.field?.id ?? null;
        if (resolvedFieldId) {
          loadFieldAndZoom(String(resolvedFieldId));
        } else {
          this.loadMapData();
        }
      });
  }

  // =========================================================================
  // FIELD DETAILS MODAL METHODS
  // =========================================================================
  private resolveRegionIdForModal(mapData: Record<string, unknown> | null | undefined): string | null {
    if (!mapData) {
      return null;
    }
    const fromData =
      mapData['regionId'] ??
      (mapData['region'] as { id?: string } | undefined)?.id ??
      (mapData['season'] as { regionId?: string } | undefined)?.regionId;
    if (fromData != null && String(fromData).trim() !== '') {
      return String(fromData);
    }
    const fromFilter = this.filterModel?.regionId;
    if (fromFilter != null && String(fromFilter).trim() !== '') {
      return String(fromFilter);
    }
    if (this.selectedRegionId != null && String(this.selectedRegionId).trim() !== '') {
      return String(this.selectedRegionId);
    }
    return null;
  }

  private handlePolygonClick(event: {
    fillColor?: string;
    lat?: number;
    lng?: number;
    data?: CropIdentificationMapItemDto;
    properties?: { data?: CropIdentificationMapItemDto };
  }): void {
    const rawData =
      event?.data ??
      event?.properties?.data ??
      null;

    if (!rawData) {
      return;
    }

    const type = FieldSourceType.Satellite;
    const model = {...rawData} as CropIdentificationMapItemDto & Record<string, unknown>;

    const polygonJson: unknown = model?.polygonJson;
    if (polygonJson) {
      if (typeof polygonJson === 'string') {
        model.polygonJson = PolygonConverterUtil.normalizePolygonWkt(polygonJson);
      } else {
        const flatCoords = PolygonConverterUtil.polygonJsonToLatLngFlat(polygonJson);
        if (flatCoords?.length) {
          model.polygonJson = PolygonConverterUtil.coordinatesLatLonToWKT(flatCoords);
          model['geometry'] = model.polygonJson;
        }
      }
    }

    if (event.lat != null && event.lng != null && !Number.isNaN(event.lat) && !Number.isNaN(event.lng)) {
      model.latitude = event.lat;
      model.longitude = event.lng;
    } else if (model.centerY != null && model.centerX != null) {
      model.latitude = Number(model.centerY);
      model.longitude = Number(model.centerX);
    }

    const regionId = this.resolveRegionIdForModal(model);
    if (regionId) {
      model['regionId'] = regionId;
    }

    const aiResultId =
      rawData.aiResultId != null && String(rawData.aiResultId).trim() !== ''
        ? String(rawData.aiResultId)
        : null;

    this.dialogService.open(ModalAcceptanceIdentification, {
      draggable: false,
      resizable: false,
      header: this.translate.instant('Crop information'),
      width: '80%',
      height: '95%',
      closable: true,
      modal: true,
      data: {
        model,
        type,
        fillColor: event.fillColor,
        regionId,
        aiResultId,
      },
    });
  }

  private handleMarkerClick(event: any): void {
    try {
      // markers send data in event?.data
      const data = event?.data || event?.properties?.data || null;
      if (!data) return;
      const item = data?.item || data;
      const aiResultId = item?.aiResultId;
      const fieldId = item?.fieldId || item?.field?.id;
      if (aiResultId || fieldId) {
        this.handlePolygonClick({
          data: item as CropIdentificationMapItemDto,
          lat: event?.lat,
          lng: event?.lng,
        });
      }

    } catch (err) {
      console.warn('Error handling marker click', err);
    }
  }

  protected export() {
    this.showModalExport = true
  }
}
